/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem,
  Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { webFormsAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../records/fieldValues'
import { entityLabel, formatRelativeTime } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import ConfirmDialog from '../common/ConfirmDialog'
import EmptyState from '../common/EmptyState'
import CopyField from './CopyField'

/** Types a stranger can fill in (the backend's list). */
const PUBLIC_TYPES = ['TEXT', 'TEXTAREA', 'NUMBER', 'DECIMAL', 'CURRENCY', 'EMAIL', 'PHONE', 'URL', 'DATE', 'DATETIME', 'CHECKBOX', 'SELECT', 'MULTI_SELECT', 'RADIO']
const linkOf = (key) => `${window.location.origin}/f/${key}`
const embedOf = (key) => `<iframe src="${linkOf(key)}" style="width:100%;max-width:600px;height:720px;border:0" title="Contact form"></iframe>`

const WebFormDialog = ({ webForm, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { forms } = useEntityTypes()
  const [v, setV] = useState(() => webForm || { formCode: forms[0]?.code || '', title: 'Contact us', intro: '', fields: [], defaults: {},
    successMessage: '', redirectUrl: '', dailyLimit: 500, enabled: true })
  const { data: form } = usePublishedForm(v.formCode || null)
  const fields = form ? allFields(form) : []
  const shown = fields.filter((f) => PUBLIC_TYPES.includes(f.fieldType))
  const optionFields = fields.filter((f) => ['SELECT', 'RADIO'].includes(f.fieldType) && !v.fields.includes(f.fieldKey))
  const missing = fields.filter((f) => f.required && f.fieldType !== 'ROLLUP' && !v.fields.includes(f.fieldKey) && v.defaults[f.fieldKey] == null && f.defaultValue == null)
  const set = (patch) => setV((x) => ({ ...x, ...patch }))
  const save = useMutation({
    mutationFn: () => (webForm ? webFormsAPI.update(webForm.id, v) : webFormsAPI.create(v)),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'web-forms'] }); notify('Web form saved'); onClose() },
  })
  const errors = save.error ? getErrorList(save.error) : []
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{webForm ? 'Edit web form' : 'New web form'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}{errors.length > 0 && <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{errors.map((e) => <li key={e}>{e}</li>)}</Box>}</Alert>}
          <TextField select label="Creates a" value={v.formCode} disabled={!!webForm} onChange={(e) => set({ formCode: e.target.value, fields: [], defaults: {} })}
            helperText={webForm ? 'Fixed once created' : undefined}>
            {forms.map((f) => <MenuItem key={f.code} value={f.code}>{entityLabel(f.entityType)} · {f.name}</MenuItem>)}
          </TextField>
          <TextField label="Title" required value={v.title} onChange={(e) => set({ title: e.target.value })} inputProps={{ maxLength: 200 }} />
          <TextField label="Intro (optional)" multiline minRows={2} value={v.intro || ''} onChange={(e) => set({ intro: e.target.value })} />
          <Autocomplete multiple options={shown.map((f) => f.fieldKey)} value={v.fields} filterSelectedOptions
            getOptionLabel={(k) => fields.find((f) => f.fieldKey === k)?.label || k} onChange={(_, list) => set({ fields: list })}
            renderInput={(p) => <TextField {...p} label="Questions (in order)" helperText="People and linked records can't be on a public form; set those with a default or an automation." />} />
          {missing.length > 0 && <Alert severity="warning">Required by the form, so add them or give them a default: {missing.map((f) => f.label).join(', ')}</Alert>}
          {optionFields.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Set on every answer (optional)</Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {optionFields.map((f) => (
                  <TextField key={f.fieldKey} select size="small" label={f.label} value={v.defaults[f.fieldKey] ?? ''}
                    onChange={(e) => {
                      const defaults = { ...v.defaults }
                      if (e.target.value === '') delete defaults[f.fieldKey]; else defaults[f.fieldKey] = e.target.value
                      set({ defaults })
                    }}>
                    <MenuItem value=""><em>Leave empty</em></MenuItem>
                    {f.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                ))}
              </Stack>
            </Box>
          )}
          <TextField label="Thank-you message (optional)" value={v.successMessage || ''} onChange={(e) => set({ successMessage: e.target.value })} inputProps={{ maxLength: 500 }}
            placeholder="Thank you! We'll be in touch." />
          <TextField label="Then go to (optional)" value={v.redirectUrl || ''} onChange={(e) => set({ redirectUrl: e.target.value })} placeholder="https://your-site.com/thanks" />
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField label="Answers per day, at most" type="number" value={v.dailyLimit} sx={{ width: 220 }}
              onChange={(e) => set({ dailyLimit: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })} />
            <FormControlLabel control={<Switch checked={v.enabled} onChange={(e) => set({ enabled: e.target.checked })} />} label="Accepting answers" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !v.title.trim() || !v.fields.length} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

const WebFormsTab = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const { data: list = [] } = useQuery({ queryKey: [tenantId, 'web-forms'], queryFn: webFormsAPI.list })
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'web-forms'] })
  const newLink = useMutation({ mutationFn: webFormsAPI.newLink, onSuccess: () => { refresh(); notify('New link made; the old one stopped working') } })
  const remove = useMutation({ mutationFn: webFormsAPI.remove, onSuccess: () => { setRemoving(null); refresh() } })
  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          A link (or embed) anyone can fill in, e.g. “Contact us” on your website. Each answer becomes a record and starts your automations.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 2 }} onClick={() => setEditing('new')}>New web form</Button>
      </Stack>
      {list.length === 0 && <Paper variant="outlined"><EmptyState title="No web forms yet" description="Make one to collect leads from your website." /></Paper>}
      <Stack spacing={2}>
        {list.map((w) => (
          <Paper key={w.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>{w.title} {!w.enabled && <Chip size="small" label="off" sx={{ ml: 1 }} />}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {w.submissions} answer{w.submissions === 1 ? '' : 's'}{w.lastSubmissionAt ? ` · last ${formatRelativeTime(w.lastSubmissionAt)}` : ''} · up to {w.dailyLimit} a day
                </Typography>
              </Box>
              <Tooltip title="Open"><IconButton href={linkOf(w.key)} target="_blank" rel="noopener" aria-label={`Open ${w.title}`}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Edit"><IconButton onClick={() => setEditing(w)} aria-label={`Edit ${w.title}`}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton onClick={() => setRemoving(w)} aria-label={`Delete ${w.title}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
            </Stack>
            <Stack spacing={1.5}>
              <CopyField label="Link" value={linkOf(w.key)} />
              <CopyField label="Embed on your site" value={embedOf(w.key)} multiline />
              <Box><Button size="small" onClick={() => newLink.mutate(w.id)}>Make a new link (the old one stops working)</Button></Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
      {editing && <WebFormDialog webForm={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!removing} title="Delete web form?" message={`“${removing?.title}” stops working everywhere it’s linked or embedded. Records it created stay.`}
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate(removing.id)} onClose={() => setRemoving(null)} />
    </Box>
  )
}

export default WebFormsTab
